import React, { useEffect } from 'react'
import { connect, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { Field, formValueSelector } from 'redux-form'

// utils
import { getPercentageFull, getRecordsUsed } from 'utils/metrics'

// components
import Modal from 'components/Shared/Modal'
import ToggleButton from 'components/Shared/Forms/ToggleButton'
import Icon from 'components/Shared/Icon'
import PlanSelect from 'components/Settings/Billing/PlanSelect'

// ducks
import {
  getAnnualToggle,
  getPaymentRouteType,
  getSubscription,
  setAnnualFlag,
  setPlanSelectedFlag,
  setSelectedPlanValue,
} from 'ducks/trialWarning'
import { getOrganization } from 'ducks/organizations'
import { loadAppsOverMetrics, getAppsOverMetrics } from 'ducks/metrics'
import { getFeatureFlag } from 'ducks/featureFlags'

const FORM_NAME = 'trialModalSignup'

const selector = formValueSelector(FORM_NAME)

const PlanSelectModal = ({
  onCancel,
  metrics,
  datasource,
  hideFreeTier,
  hideStorage,
  title,
  paymentRouteType,
  onDowngrade,
  onClickBackChangePlan,
  annual,
  setAnnualFlag,
  setPlanSelectedFlag,
  subscription = {},
  setSelectedPlanValue,
  isOrganizationAdmin,
  organizationName,
  userId,
  appsOver,
  hasTeamSwitcher,
}) => {
  const { planType } = subscription

  const selectedPlan = useSelector(state => selector(state, 'plan'))

  useEffect(() => {
    if (userId && planType) {
      loadAppsOverMetrics(userId)
    }
  }, [userId])

  const downgrade = paymentRouteType === 'billing' && !selectedPlan && planType

  const modalDesign =
    paymentRouteType === 'upgrade'
      ? 'modal-plan-select'
      : 'modal-plan-select-billing'

  const percentFull =
    metrics && datasource ? getPercentageFull(metrics, datasource) : null

  const recordsUsed =
    metrics && datasource ? getRecordsUsed(metrics, datasource) : null

  const { planInterval } = subscription

  const onToggle = () => setAnnualFlag(!annual)

  const onClickNext = () => {
    setPlanSelectedFlag(true)
    setSelectedPlanValue(selectedPlan)
  }

  const shouldBeDisabled = () => {
    // disable if a plan is not selected
    if (!selectedPlan) return true

    // disable if the current maker is not an admin of the
    // current organization
    if (!isOrganizationAdmin) return true

    // disable if the selected plan is their current plan
    // with no changes to billing interval
    if (selectedPlan === planType) {
      if (planInterval === 'year' && annual) return true
      if (planInterval !== 'year' && !annual) return true
    }

    return false
  }

  const isDisabled = shouldBeDisabled()

  let paddingLeft
  let paddingRight
  let paddingTop = '20px'

  if (paymentRouteType === 'billing') {
    paddingLeft = '20px'
    paddingRight = '20px'
    paddingTop = '10px'
  }

  if (hasTeamSwitcher) {
    paddingTop = '0'
  }

  let tooltip = null

  if (!isOrganizationAdmin) {
    if (organizationName) {
      tooltip = `You must be an admin of the organization "${organizationName}" in order to upgrade.`
    } else {
      tooltip = 'You need to be an admin for this organization to change plans.'
    }
  }

  if (hasTeamSwitcher) {
    return (
      <>
        {paymentRouteType === 'upgrade' && <Modal.Header title={title} />}
        <Modal.Content style={{ paddingTop }}>
          {!hideStorage && paymentRouteType === 'upgrade' && (
            <div className="trial-signup-modal-storage">
              <h2>{`${percentFull} of free records used`}</h2>
              <p className="deemphasize">{`You’ve used ${recordsUsed} records included with the free plan.`}</p>
            </div>
          )}
          <div className="planselect-monthly-annual">
            <p>Monthly</p>
            <ToggleButton value={annual} onChange={onToggle} />
            <p>Yearly</p>
            <div className="toggle-offer">
              <Icon type="keyboard-back" />
              <p>Save 20%</p>
            </div>
          </div>

          <div className={modalDesign}>
            <Field
              name="plan"
              component={PlanSelect}
              hideFreeTier={hideFreeTier}
              annual={annual}
              onClick={onClickNext}
              downgrade={downgrade}
              appsOver={appsOver}
            />
          </div>
        </Modal.Content>
        <div className="planselect-actions">
          {paymentRouteType === 'upgrade' && onCancel ? (
            <Modal.Button type="button" text onClick={onCancel}>
              Cancel
            </Modal.Button>
          ) : (
            planType && (
              <Modal.Button type="button" text onClick={onClickBackChangePlan}>
                Back
              </Modal.Button>
            )
          )}
          {downgrade ? (
            <Modal.Button large type="button" onClick={onDowngrade}>
              Downgrade to Free
            </Modal.Button>
          ) : (
            <Modal.Button
              large
              type="button"
              onClick={onClickNext}
              disabled={isDisabled}
              tooltip={tooltip}
            >
              Next
            </Modal.Button>
          )}
        </div>
      </>
    )
  }

  return (
    <>
      {paymentRouteType === 'upgrade' && <Modal.Header title={title} />}
      <Modal.Content style={{ paddingLeft, paddingRight, paddingTop }}>
        {!hideStorage && paymentRouteType === 'upgrade' && (
          <div className="trial-signup-modal-storage">
            <h2>{`${percentFull} of free records used`}</h2>
            <p className="deemphasize">{`You’ve used ${recordsUsed} records included with the free plan.`}</p>
          </div>
        )}
        <div className="monthly-annual-planselect">
          <p>Monthly</p>
          <ToggleButton value={annual} onChange={onToggle} />
          <p>Yearly</p>
          <div className="toggle-offer">
            <Icon type="keyboard-back" />
            <p>Save 20%</p>
          </div>
        </div>

        <div className={modalDesign}>
          <Field
            name="plan"
            component={PlanSelect}
            hideFreeTier={hideFreeTier}
            annual={annual}
            onClick={onClickNext}
            downgrade={downgrade}
            appsOver={appsOver}
          />
        </div>
      </Modal.Content>
      <Modal.Actions>
        {paymentRouteType === 'upgrade' && onCancel ? (
          <Modal.Button type="button" text onClick={onCancel}>
            Cancel
          </Modal.Button>
        ) : (
          planType && (
            <Modal.Button type="button" text onClick={onClickBackChangePlan}>
              Back
            </Modal.Button>
          )
        )}
        {downgrade ? (
          <Modal.Button large type="button" onClick={onDowngrade}>
            Downgrade to Free
          </Modal.Button>
        ) : (
          <Modal.Button
            large
            type="button"
            onClick={onClickNext}
            disabled={isDisabled}
            tooltip={tooltip}
          >
            Next
          </Modal.Button>
        )}
      </Modal.Actions>
    </>
  )
}

PlanSelectModal.propTypes = {
  title: PropTypes.string,
}

PlanSelectModal.defaultProps = {
  title: 'Upgrade Your Account',
}

const mapStateToProps = (state, { organizationId }) => {
  const organization = getOrganization(state, organizationId)

  const userId = state.users.currentUser.id

  return {
    annual: getAnnualToggle(state),
    subscription: getSubscription(state, organizationId),
    paymentRouteType: getPaymentRouteType(state),
    isOrganizationAdmin: organization?.OrganizationUser.isAdmin,
    organizationName: organization?.name,
    userId,
    appsOver: getAppsOverMetrics(state, userId),
    hasTeamSwitcher: getFeatureFlag(state, 'hasTeamSwitcher'),
  }
}

const connected = connect(mapStateToProps, {
  loadAppsOverMetrics,
  setAnnualFlag,
  setPlanSelectedFlag,
  setSelectedPlanValue,
})(PlanSelectModal)

export default connected
